import React, { useState, useMemo } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/PoolBookingFields.json";
import {
    GetAll,
    Delete,
    GetById
} from "adminApi/PoolBookingApi/PoolBooking.js";

const bookingStatusMap = {
    confirmed: "success",
    pending:   "warning",
    cancelled: "danger",
    completed: "info",
};
const paymentStatusMap = {
    paid:      "success",
    unpaid:    "danger",
    partial:   "warning",
    refunded:  "secondary",
};

const PoolBookingListTable = () => {
    const [search, setSearch] = useState("");
    const [limit, setLimit] = useState(10);
    const [page, setPage] = useState(1);
    const debouncedSearch = useDebouncedValue(search, 500);
    const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
    const sizeOptions = [10, 20, 50, 100];
    const { tabledata, poolPrintKeys } = fields;

    // Fetch Hook
    const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
        page,
        limit,
        search: debouncedSearch,
        title: "Pool Booking List",
    });

    const displayFields = generateDisplayFields(tabledata);

    // Format booking_status and payment_status as colored badges
    const formattedPoolData = useMemo(() => data.map(item => ({
        ...item,
        booking_status: item.booking_status ? (
            <span className={`badge bg-${bookingStatusMap[item.booking_status] || "secondary"}`}>
                {item.booking_status}
            </span>
        ) : "",
        payment_status: item.payment_status ? (
            <span className={`badge bg-${paymentStatusMap[item.payment_status] || "secondary"}`}>
                {item.payment_status}
            </span>
        ) : "",
    })), [data]);

    // Table Hook
    const { formattedData, columns } = useTableConfig({
        data: formattedPoolData,
        page,
        limit,
        canShowAction,
        refetch,
        isPrint: true,
        IsDelete: true,
        config: {
            entityName: "Pool Booking",
            editUrl: "edit-pool-booking",
            idKey: "p_booking_id",
            deleteApi: Delete,
            PrintApi: GetById,
            displayFields,
            PrintKeys: poolPrintKeys,
        },
    });

    const handleSearch = (val, resetPage = null) => {
        setSearch(val);
        if (resetPage !== null) setPage(resetPage);
    };

    const onPageChange = (p) => setPage(p);
    const onLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
    };

    return (
        <DataTable
            columns={columns}
            data={formattedData}
            options={{
                loading,
                error,
                sortableColumns: displayFields.map((f) => f.accessor),
                copy: true,
                csv: true,
                pdf: true,
                print: true,
                fileName: "Pool Booking",
            }}
            Search={{
                search: true,
                SearchInputValue: search,
                handleSearch: handleSearch,
            }}
            Entries={{
                Entries: true,
                limit,
                onLimitChange,
                options: sizeOptions,
            }}
            pagination={{
                isActive: true,
                meta,
                onPageChange,
            }}
        />
    );
};

export default PoolBookingListTable;
